import React, { useMemo } from 'react';
import { APIReferenceMember, MemberType, ReferenceType } from '@modules/client';
import { Grid, Table, TableBody, TableCell, TableRow, Typography } from '@rbx/ui';
import { ApiTag } from '@modules/ui/tags/types';
import useReferenceSummaryMemberTableStylesV2 from './ReferenceSummaryMemberTableV2.styles';
import { getSummaryTableId } from '../../utils/apiReferencePageNavUtils';
import ReferenceSummaryMemberTrTitle from './ReferenceSummaryMemberTrTitle';
import { hasDeprecatedTag } from '../utils/tagUtils';
import ReferenceMemberTags from './ReferenceMemberTags';
import ReferenceSummarySectionTitle from './ReferenceSummarySectionTitle';
import getReferenceMemberIcon from './getReferenceMemberIcon';

interface ReferenceSummaryMemberTableV2Props {
  memberType: MemberType;
  referenceType: ReferenceType;
  referenceName: string;
  title?: string;
  isInheritedMembers?: boolean;
  ariaLabel?: string;
  members: APIReferenceMember[];
  isEventMemberType: boolean;
}

export default function ReferenceSummaryMemberTableV2({
  memberType,
  title,
  referenceName,
  isInheritedMembers,
  ariaLabel = '',
  referenceType,
  members,
  isEventMemberType = false,
}: ReferenceSummaryMemberTableV2Props) {
  if (!title && !ariaLabel)
    throw new Error(`Missing title or aria-label on reference member table`);
  const {
    classes: {
      tableContainer,
      memberText,
      deprecatedMemberText,
      table,
      tableRow,
      cellContent,
      iconWrapper,
    },
  } = useReferenceSummaryMemberTableStylesV2();

  // Memoize the icon to avoid recreating it on every render
  // All members of the same type share the same icon
  const memberIcon = useMemo(() => getReferenceMemberIcon(memberType), [memberType]);

  return (
    <Grid
      className={tableContainer}
      id={!isInheritedMembers ? getSummaryTableId(memberType) : undefined}>
      {title && members.length > 0 && <ReferenceSummarySectionTitle title={title} />}
      {members.length > 0 && (
        <Table className={table}>
          <TableBody>
            {members.map((member, i) => {
              const isDeprecated = hasDeprecatedTag(member.tags) || !!member.deprecationMessage;
              return (
                <TableRow key={member.name} className={tableRow}>
                  <TableCell>
                    <div className={cellContent}>
                      <div className={iconWrapper}>{memberIcon}</div>
                      <Typography
                        variant='body2'
                        component='span'
                        className={isDeprecated ? deprecatedMemberText : memberText}>
                        <ReferenceSummaryMemberTrTitle
                          member={member}
                          referenceType={referenceType}
                          referenceName={referenceName}
                          isInheritedMember={isInheritedMembers}
                          isEventMemberType={isEventMemberType}
                          isSameNameAsPrevMember={member.name === members[i - 1]?.name}
                          engineRevampEnabled
                        />
                      </Typography>
                      {isDeprecated && (
                        <React.Fragment>
                          {' '}
                          <span>
                            <ReferenceMemberTags
                              tags={[ApiTag.Deprecated]}
                              member={member}
                              engineRevampEnabled
                            />
                          </span>
                        </React.Fragment>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Grid>
  );
}
